import { forwardRef, useCallback } from 'react';

import { Box, Divider, Flex } from '@chakra-ui/react';
import { allLevels } from 'components/RabbitOKRCreationPanel/PrimaryPanel/V2/sections/OtherDetails';
import { useOKRAccessControl } from 'components/RabbitOKRSidePanel/AccessControl/useOKRAccessControl';
// eslint-disable-next-line import/no-cycle
import { DetailsSectionHeader } from 'components/RabbitOKRSidePanel/Components/SectionHeaders';
import { isDependentType } from 'components/RabbitOKRSidePanel/SecondaryPanel/OtherDetailsPanel/sections/OKRTrackingTypeSection';
import { useOKRTagsQuery } from 'components/RabbitOKRSidePanel/datahooks/useGetCustomTagsOrganization';
import { openChangeDetailsPanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import useOKRConfig from 'components/hooks/useOKRConfig';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { selectOrgData } from 'selectors/orgData';

import {
  CalendarThin,
  Collaborators,
  DepartmentName,
  DepartmentThin,
  Description,
  Hashtag,
  PeerSettingsV2,
  PrivateObjective,
  Realign,
  Tags,
  WeightageTracking,
} from 'assets/assetsV1/iconComponents';
import { MeshMavenWithYellowBackground } from 'assets/assetsV1/iconComponents/MeshMavenWithYellowBackground';

import {
  GOAL_VISIBILITY_OPTIONS,
  OBJECTIVE_LEVEL,
  OBJECTIVE_TYPES_MAP,
  OKR_PROGRESS_TYPES,
  OKR_TYPES,
} from 'constants/ProjectConstants';
import { getOrgGoalMeasurementTypes } from 'constants/helper';
import { sendChangeDetailsClickedEvent } from 'constants/mixpanelHelper';
import { GOALS_EVENT_SOURCES } from 'constants/trackingEvents';

import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { OKROwnerAndCollaboratorComponent, OWNER_TYPES } from './OKROwnerAndCollaborator';
import { OKRTags } from './OKRTags';

const Details = ({ isOKRPanelDisabled }) => {
  const okrDetails = useSelector(kRDrawerSelector);
  const {
    parentPost,
    timeBoundDetails,
    visibilityType,
    toIds = [],
    startTS,
    dueTS,
    description,
    uuid,
    measure = 'PERCENTAGE',
    measureType = 'Progress',
    startingProgressValue,
    target,
    progressType,
    objectiveLevel,
    departmentName,
    ruleId,
  } = okrDetails;

  const { data: okrCustomTagsData } = useOKRTagsQuery({ okrId: uuid });
  const isObjective = okrDetails?.type === OKR_TYPES.OBJECTIVE;
  const { OBJECTIVE_TYPE } = useOKRConfig();

  const dispatch = useDispatch();

  const formattedStartDate = startTS ? format(new Date(startTS), 'MMM dd, yyyy') : null;

  const formattedDueDate = dueTS ? format(new Date(dueTS), 'MMM dd, yyyy') : null;

  const collaborators = toIds
    .filter(({ ownerType }) => ownerType === OWNER_TYPES.COLLABORATOR)
    .map((emp) => ({ ...emp, isSelected: true }));

  const owners = toIds
    .filter(({ ownerType }) => ownerType === OWNER_TYPES.OWNER)
    .map((emp) => ({ ...emp, isSelected: true }));

  const collaboratorsExist = collaborators.length > 0;

  const showOwnersName = owners.length <= 1;

  const levelLabel = allLevels?.find((level) => level?.value === objectiveLevel);

  const visibilityLabel = GOAL_VISIBILITY_OPTIONS.find((option) => option.value === visibilityType)?.label;

  const orgData = useSelector(selectOrgData);
  const kRMeasurementTypes = getOrgGoalMeasurementTypes(orgData?.currency ?? null);
  const selectedMeasure = kRMeasurementTypes.find(({ code }) => code === measure);
  const isDetailedObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.DETAILED.value;

  const isCompactObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.COMPACT.value;
  const showOwnersAndCollaborators = !isObjective || isCompactObjectiveType || isDetailedObjectiveType;
  const showTrackingSection = !isObjective || isDetailedObjectiveType;

  const showTags = okrCustomTagsData?.length > 0;
  const showDescription = description?.length > 0;
  const { canEditOKR } = useOKRAccessControl();
  const { keyResultsName, objectivesName } = useOrganisationSettings();

  const handleClickOnChangeDetails = useCallback(() => {
    sendChangeDetailsClickedEvent({
      eventProperties: {
        goalId: uuid,
        source: 'Change Details Button',
        type: GOALS_EVENT_SOURCES.EDIT_GOAL_PANEL,
      },
    });
    dispatch(openChangeDetailsPanel({}));
  }, [dispatch]);

  return (
    <Flex flexDirection="column" alignItems="stretch" flexWrap="nowrap" w="100%">
      <DetailsSectionHeader
        handleClickOnChangeDetails={handleClickOnChangeDetails}
        canEditOKR={canEditOKR && !isOKRPanelDisabled}
      />
      <Divider sx={{ borderColor: '#CFD7DF' }} marginBottom="12px" />

      <Box display="flex" flexDirection="column" alignItems="flex-start" gap="2px" alignSelf="stretch">
        <Box display="flex" padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
          <Tooltip label="Parent" shouldWrapChildren>
            <Realign boxSize="24px" color="text.g1" />
          </Tooltip>
          <TextBodyMedium>{parentPost?.text || 'None'}</TextBodyMedium>
        </Box>
        <Box display="flex" padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
          <Tooltip label="Time Period" shouldWrapChildren>
            <CalendarThin boxSize="24px" color="text.g1" fill="none" />
          </Tooltip>
          <TextBodyMedium>
            {timeBoundDetails?.timelineTag} : {formattedStartDate} to {formattedDueDate}
          </TextBodyMedium>
        </Box>

        {showOwnersAndCollaborators && (
          <Box display="flex" width="100%">
            <Flex padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
              <Tooltip label="Owners" shouldWrapChildren>
                <PeerSettingsV2 boxSize="24px" fill="transparent" color="text.g1" />
              </Tooltip>
              <Box
                sx={{
                  '.owners-collabs > div': {
                    height: '24px',
                  },
                }}
              >
                <OKROwnerAndCollaboratorComponent
                  isOwner
                  ownersAndCollaborators={toIds}
                  isReadOnly
                  showName={showOwnersName}
                  showSelectEmptyMessage={false}
                />
              </Box>
            </Flex>
            <Flex padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
              <Tooltip label="Collaborators" shouldWrapChildren>
                <Collaborators fill="transparent" boxSize="24px" color="text.g1" />
              </Tooltip>
              {collaboratorsExist ? (
                <Box
                  sx={{
                    '.owners-collabs > div': {
                      height: '24px',
                    },
                  }}
                >
                  <OKROwnerAndCollaboratorComponent
                    isOwner={false}
                    ownersAndCollaborators={toIds}
                    isReadOnly
                    showName={false}
                    showSelectEmptyMessage={false}
                  />
                </Box>
              ) : (
                <TextBodyMedium color="text.g1">No Collaborators </TextBodyMedium>
              )}
            </Flex>
          </Box>
        )}
        <Box display="flex" padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
          <Tooltip label="Visibility" shouldWrapChildren>
            <PrivateObjective boxSize="24px" color="text.g1" />
          </Tooltip>
          <TextBodyMedium>{visibilityLabel}</TextBodyMedium>
        </Box>
        {isObjective === true && (
          <Box display="flex" padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
            <Tooltip label="Level" shouldWrapChildren>
              <DepartmentThin boxSize="24px" color="text.g1" fill="none" />
            </Tooltip>
            <TextBodyMedium color="text.g1">{levelLabel?.label}</TextBodyMedium>
          </Box>
        )}
        {isObjective === true && objectiveLevel === OBJECTIVE_LEVEL.DEPARTMENT && departmentName ? (
          <Box display="flex" padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
            <Tooltip label="Department" shouldWrapChildren>
              <DepartmentName boxSize="24px" color="text.g1" fill="none" />
            </Tooltip>
            <TextBodyMedium color="text.g1">{departmentName}</TextBodyMedium>
          </Box>
        ) : null}
        {showTrackingSection && progressType === OKR_PROGRESS_TYPES.MANUAL ? (
          <Box display="flex" padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
            <Tooltip label="Metrics" shouldWrapChildren>
              <Hashtag boxSize="24px" color="text.g1" />
            </Tooltip>
            <TextBodyMedium>
              {measureType || 'Progress'} : Start value {`${startingProgressValue + selectedMeasure.symbol} `}
              to Target value {target + selectedMeasure.symbol}
            </TextBodyMedium>
          </Box>
        ) : null}
        {showTrackingSection && (
          <Box display="flex" padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
            <Tooltip label="Tracking Type" shouldWrapChildren>
              <WeightageTracking boxSize="24px" color="text.g1" fill="none" />
            </Tooltip>
            <TextBodyMedium>{`${
              isDependentType(progressType)
                ? `Dependent tracking computed as  ${
                    progressType === OKR_PROGRESS_TYPES.DERIVED_SUM ? 'Sum' : 'Average'
                  }`
                : 'Independent'
            }`}</TextBodyMedium>
          </Box>
        )}
        {showTags && (
          <Box display="flex" padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
            <Tooltip label="Tags" shouldWrapChildren>
              <Tags boxSize="24px" color="text.g1" fill="none" />
            </Tooltip>
            <Box
              width="100%"
              sx={{
                '.okr-tags-popover > div > div:first-child': {
                  border: 'none',
                  padding: '0px',
                  height: 'auto',
                },
                '.okr-tags-popover .chakra-stack': { padding: '0px' },
              }}
            >
              <OKRTags
                tags={okrCustomTagsData}
                containerProps={{ maxWidth: '408px', className: 'okr-tags-popover' }}
                isReadOnly
                showNewTagCreation={false}
                tagNameLengthLimit={20}
                okrId={uuid}
              />
            </Box>
          </Box>
        )}
        {showDescription && (
          <Box display="flex" padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
            <Tooltip label="Description" shouldWrapChildren>
              <Description boxSize="24px" color="text.g1" fill="none" alignSelf="flex-start" />
            </Tooltip>
            <TextBodyMedium
              as="p"
              sx={{
                whiteSpace: 'pre-wrap',
                overflowX: 'scroll',
                display: '-webkit-box',
                '-webkit-line-clamp': '10',
                '-webkit-box-orient': 'vertical',
              }}
            >
              {description}
            </TextBodyMedium>
          </Box>
        )}
        {ruleId && (
          <Box display="flex" padding="8px 12px" alignItems="center" gap="8px" flex="1 0 0">
            <Tooltip label={`Role based ${isObjective ? objectivesName : keyResultsName}`} shouldWrapChildren>
              <MeshMavenWithYellowBackground boxSize="14px" mx="4px" />
            </Tooltip>
            <TextBodyMedium>{`Role based ${isObjective ? objectivesName : keyResultsName}`}</TextBodyMedium>
          </Box>
        )}
      </Box>
    </Flex>
  );
};

export default Details;

export const RefableDetails = forwardRef((props, ref) => (
  <Box ref={ref} w="100%">
    <Details {...props} />
  </Box>
));
